import { CaretDownSolidIcon, MagnifyingGlassRegularIcon } from "@elixir/icons";
import { SUBJECT_KEYS, TAptFieldOption } from "@elixir/types";
import { colorsToBeChosen } from "@elixir/utils";
import { Dropdown } from "antd";
import { useEffect, useState } from "react";
import { OptionChoice, OptionsSelectUi } from "ui";
import { showAccessDeniedError } from "../../../../utils/bridges/methods";

type TSelectionDropdownProps = {
  searchQuery: string;
  onSearch: (query: string) => void;
  dropdownOptions: TAptFieldOption[];
  filteredOptions: TAptFieldOption[];
  type: "single" | "multi";
  title: string;
  onOptionSelect: (options: TAptFieldOption[]) => void;
  onDropdownAction?: (value: boolean) => void;
  aclSubjectKey?: SUBJECT_KEYS;
  isDisabled?: boolean;
};

const SelectionDropdown = ({
  searchQuery,
  onSearch,
  dropdownOptions,
  filteredOptions,
  type,
  title,
  onOptionSelect,
  onDropdownAction,
  aclSubjectKey,
  isDisabled = false,
}: TSelectionDropdownProps) => {
  const [selectedOptions, setSelectedOptions] = useState<TAptFieldOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    setSelectedOptions(dropdownOptions?.filter((option) => option.isSelected));
  }, [dropdownOptions]);

  const onChange = (value: boolean) => {
    onDropdownAction?.(value);
    setShowDropdown(value);
  };

  return (
    <Dropdown
      placement="bottom"
      className="dr-cursor-pointer"
      menu={{
        items: dropdownOptions?.map((option) => ({
          key: option.id,
          label: `${option.value} ${option.description?.trim() ? `(${option.description})` : ""} 
          `,
        })),
      }}
      trigger={["click"]}
      open={showDropdown}
      onOpenChange={(value) => {
        !isDisabled && onChange(value);
      }}
      dropdownRender={() => (
        <div className="dr-bg-darwin-touch-neutral-0 dr-rounded-4 dr-shadow-elevation-50">
          <div className="dr-px-16 dr-py-10 dr-flex dr-items-center dr-gap-8 dr-border-b dr-border-darwin-touch-neutral-200">
            <MagnifyingGlassRegularIcon className="dr-w-13 dr-h-13 dr-text-darwin-touch-neutral-800" />
            <input
              type="text"
              placeholder="Search..."
              className="dr-placeholder-darwin-touch-neutral-800"
              value={searchQuery}
              onChange={(e) => {
                onSearch(e.target.value);
              }}
            />
          </div>
          <div className="dr-max-h-210 dr-overflow-y-auto">
            {filteredOptions.map((option) => {
              return (
                <div className="dr-px-16" key={`option-wrapper-${option.id}`}>
                  <OptionChoice
                    title={`${option.value}${option.description ? ` (${option.description})` : ""} `}
                    checked={!!selectedOptions.find((o) => o.id === option.id)}
                    onSelect={() => {
                      let optionsToPush: TAptFieldOption[];
                      if (type === "single") {
                        optionsToPush = selectedOptions.find(
                          (o) => o.id === option.id
                        )
                          ? selectedOptions.filter((o) => o.id !== option.id)
                          : [option];
                        setSelectedOptions(optionsToPush);
                      } else {
                        optionsToPush = selectedOptions.find(
                          (o) => o.id === option.id
                        )
                          ? selectedOptions.filter((o) => o.id !== option.id)
                          : [...selectedOptions, option];

                        setSelectedOptions(optionsToPush);
                      }
                      onOptionSelect(optionsToPush);
                    }}
                    type={type === "single" ? "radio" : "checkbox"}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    >
      <div
        onClick={() => {
          !isDisabled && onChange(true);
        }}
        className="dr-flex-1 dr-max-w-165"
      >
        <OptionsSelectUi
          title={(title ?? "").replace(/_/g, " ")}
          options={
            selectedOptions?.map((option) => {
              const requiredColor = colorsToBeChosen.find(
                (color) => color.textColor === option.color
              ) || {
                bgColor: "#FFF",
                textColor: "#1A1A1A",
              };
              return {
                option_id: option.id.toString(),
                label: option.value,
                hex: option.color || requiredColor.textColor,
                bg_hex: option.bgColor || requiredColor.bgColor,
              };
            }) || []
          }
          selectedOptions={selectedOptions.map((o) => o.id.toString())}
          textStyle="dr-text-darwin-touch-neutral-600"
          bgStyle="dr-bg-darwin-touch-neutral-50"
          type={type}
          SuffixIcon={
            type === "single" ? (
              <CaretDownSolidIcon className="dr-w-8 dr-h-12 dr-text-darwin-touch-neutral-600" />
            ) : undefined
          }
        />
      </div>
    </Dropdown>
  );
};

export default SelectionDropdown;
